import { createContext, useCallback, useContext, type ReactNode } from 'react'
import { useWords } from '../lib/i18n/useWords'
import { t, type StaffKey, type StaffLanguage, type Vars } from '../lib/staffText'

// The reader's language, handed down once rather than threaded through every
// component. English outside a provider, which is what every staff screen said
// before there was a choice — so a component rendered somewhere nobody thought to
// wrap still says something a person can read.
//
// The way to change it travels with it. There is one place a language is chosen —
// the menu in the top bar — and that menu sits on every staff screen, so the
// screens would otherwise each have to be handed the same callback by hand.
interface StaffLanguageValue {
  language: StaffLanguage
  choose?: (lang: StaffLanguage) => Promise<void>
  /** Bumps when a language file arrives or the organisation's words change. */
  words?: number
}

const StaffLanguageContext = createContext<StaffLanguageValue>({ language: 'en' })

export function StaffLanguageProvider({
  language,
  onChoose,
  children,
}: {
  language: StaffLanguage
  onChoose?: (lang: StaffLanguage) => Promise<void>
  children: ReactNode
}) {
  const words = useWords(language)
  return <StaffLanguageContext.Provider value={{ language, choose: onChoose, words }}>{children}</StaffLanguageContext.Provider>
}

/** `say(key, vars?)` in the reader's language, and the language itself for the lib functions that take one. */
export function useStaffText(): { lang: StaffLanguage; say: (key: StaffKey, vars?: Vars) => string } {
  const { language: lang, words } = useContext(StaffLanguageContext)
  const say = useCallback((key: StaffKey, vars?: Vars) => t(key, lang, vars), [lang, words])
  return { lang, say }
}

/** The one way to change the reader's language, or undefined where there is none (a screen outside the app). */
export function useLanguageChoice(): ((lang: StaffLanguage) => Promise<void>) | undefined {
  return useContext(StaffLanguageContext).choose
}
